import { useEffect } from 'react';

const APP_SUFFIX = 'Arbi Dev';

/**
 * Sets document.title for the lifetime of the calling component.
 * Concatenates as `${page} — ${APP_SUFFIX}` when page is provided;
 * falls back to just APP_SUFFIX on home/landing pages where the
 * caller passes nothing or empty string. Restores the previous
 * title on unmount.
 *
 * Per the locked naming convention (Sol 2026-05-06):
 *   - arbi root  → APP_SUFFIX = 'Arbi'      (consumer count: 0 today;
 *                                             ~48-surface retrofit per
 *                                             Phase 0 disk inventory
 *                                             deferred to BACKLOG line
 *                                             1178 sub-bullet, post-launch)
 *   - arbi-dev   → APP_SUFFIX = 'Arbi Dev'  (this hook)
 *   - arbi-docs  → APP_SUFFIX = 'Arbi Docs' (Day 65)
 *
 * Each app has its own copy of this hook with the suffix hardcoded
 * as a module constant — three small files, zero shared-state
 * coupling, zero env-var or runtime detection.
 *
 * Static fallback in each app's index.html carries the same suffix-
 * only text, so a hard refresh on a page where the hook hasn't yet
 * fired briefly shows the app name before the hook overrides.
 */
export function useDocumentTitle(page?: string): void {
  useEffect(() => {
    const prev = document.title;
    document.title = page && page.trim() ? `${page} — ${APP_SUFFIX}` : APP_SUFFIX;
    return () => {
      document.title = prev;
    };
  }, [page]);
}
